import type { ChatMessage } from "../messages/types";
import type { ChatMessageDTO, ChatWithMessagesDTO } from "../server";
import type { LoadedChat } from "../types";
import type { PersistedToolProgressPayload } from "@/backend/analysis-chat-v2/types";
import type { ReportItem } from "@/components/analysis/agentic/db_types";
import { toToolProgressTimelineItems } from "@/components/analysis/chat/v2/utils/statusMessages";
import type {
  AnalysisChatV2FileRef,
  FrontendChatId,
  FrontendChatMessageId,
} from "@/utils/types";

type PersistedChatMessageDTO<U extends FrontendChatMessageId> =
  | {
      id: U;
      timestamp: number;
      role: "user";
      text: string;
      files?: AnalysisChatV2FileRef[];
    }
  | {
      id: U;
      timestamp: number;
      role: "assistant";
      iterations: Array<{
        reportItems: ReportItem[];
        statusMessages: PersistedToolProgressPayload[];
        error?: string;
        timestamp: number;
      }>;
      langsmithTraceId?: string;
    };

export const convertMessages = <U extends FrontendChatMessageId>(
  messages: ChatMessageDTO<U>[]
): ChatMessage<U>[] => {
  return messages.map((message) => {
    if (message.role === "user") {
      return {
        ...message,
        timestamp: new Date(message.timestamp),
        files: message.files,
      };
    } else {
      // Assistant message with iterations
      return {
        ...message,
        iterations: message.iterations.map((iteration) => ({
          ...iteration,
          timestamp: new Date(iteration.timestamp),
        })),
        timestamp: new Date(message.timestamp),
        langsmithTraceId: message.langsmithTraceId,
      };
    }
  });
};

export const convertPersistedMessages = <U extends FrontendChatMessageId>(
  messages: PersistedChatMessageDTO<U>[]
): ChatMessage<U>[] =>
  convertMessages(
    messages.map((message): ChatMessageDTO<U> => {
      if (message.role === "user") {
        return message;
      }
      return {
        ...message,
        iterations: message.iterations.map((iteration) => ({
          ...iteration,
          statusMessages: toToolProgressTimelineItems(iteration.statusMessages),
        })),
      };
    })
  );

export const convertSession = <
  T extends FrontendChatId,
  U extends FrontendChatMessageId,
>(
  session: ChatWithMessagesDTO<T, U>
): LoadedChat<T, U> => ({
  id: session.id,
  title: session.title,
  createdAt: new Date(session.createdAt),
  updatedAt: new Date(session.updatedAt),
  messages: convertMessages(session.messages),
  isPersisted: true,
  agentRunId: session.agentRunId,
  isLegacy: session.isLegacy,
  isReadOnly: session.isReadOnly,
  slackChannelName: session.slackChannelName,
  surveyIds: session.surveyIds,
  pendingMessageId: session.pendingMessageId,
});
